import { DayLabel } from '@shared/components'
import { useLocales } from '@shared/locales'
import {
  Button,
  Descriptions,
  Flex,
  Modal,
  Table,
  TableColumnsType
} from 'antd'

import { PaymentAccount, PaymentAccountDetailsModalProps } from './types'

const formatPrice = (price: number, currency: string) => {
  return `${price.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${currency}`
}

const formatAmountInWords = (amount: number, currency: string): string => {
  // Simplified version - in production would use a proper number-to-words library
  if (amount === 1555) {
    return 'o mie cinci sute cincizeci și cinci'
  }

  const wholePart = Math.floor(amount)

  return `${wholePart} ${currency}`
}

export const PaymentAccountDetailsModal = ({
  paymentAccount,
  onCancel,
  ...props
}: PaymentAccountDetailsModalProps) => {
  const { t } = useLocales()

  if (!paymentAccount) return null

  const serviceColumns: TableColumnsType<PaymentAccount['services'][0]> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 50
    },
    {
      title: 'Descriere',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: 'Cantitate',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100
    },
    {
      title: 'Preț',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (price) => formatPrice(price, paymentAccount.currency)
    },
    {
      title: 'Suma fără TVA',
      dataIndex: 'amount_without_vat',
      key: 'amount_without_vat',
      width: 130,
      render: (amount) => formatPrice(amount, paymentAccount.currency)
    },
    {
      title: 'Inclusiv TVA',
      dataIndex: 'vat_amount',
      key: 'vat_amount',
      width: 120,
      render: (vat) => formatPrice(vat, paymentAccount.currency)
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (total) => formatPrice(total, paymentAccount.currency)
    }
  ]

  return (
    <Modal
      width={1000}
      maskClosable={false}
      title={`Cont de plată Nr. ${paymentAccount.account_number}`}
      cancelText={t('buttons.close')}
      onCancel={onCancel}
      footer={null}
      {...props}
    >
      <div style={{ padding: '20px 0' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0 }}>{paymentAccount.supplier_name}</h2>
          <div
            style={{
              marginTop: '16px',
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <div>
              <div>
                <strong>CONT DE PLATĂ Nr.</strong>
              </div>
              <div
                style={{
                  backgroundColor: '#ffeb3b',
                  padding: '4px 8px',
                  display: 'inline-block',
                  marginTop: '4px'
                }}
              >
                {paymentAccount.account_number}
              </div>
            </div>
            <div>
              <div>
                <strong>Date</strong>
              </div>
              <div
                style={{
                  backgroundColor: '#ffeb3b',
                  padding: '4px 8px',
                  display: 'inline-block',
                  marginTop: '4px'
                }}
              >
                {new Date(paymentAccount.date).toLocaleDateString('ro-RO')}
              </div>
            </div>
          </div>
        </div>

        {/* Supplier Information */}
        <div
          style={{
            backgroundColor: '#1976d2',
            color: 'white',
            padding: '12px',
            marginBottom: '16px'
          }}
        >
          <strong>FURNIZOR: {paymentAccount.supplier_name}</strong>
        </div>
        <Descriptions
          bordered
          column={2}
          size="small"
          style={{ marginBottom: '16px' }}
          items={[
            {
              label: 'Adresă',
              children: paymentAccount.supplier_address
            },
            {
              label: 'C/f',
              children: paymentAccount.supplier_fiscal_code
            },
            {
              label: 'IBAN',
              children: paymentAccount.supplier_iban
            },
            {
              label: 'Bancă',
              children: paymentAccount.supplier_bank
            },
            {
              label: 'C/b',
              children: paymentAccount.supplier_bank_code
            },
            {
              label: 'TVA',
              children: paymentAccount.supplier_vat
            }
          ]}
        />

        {/* Client Information */}
        <div
          style={{
            backgroundColor: '#ffeb3b',
            padding: '12px',
            marginBottom: '16px'
          }}
        >
          <strong>CLIENT: {paymentAccount.client_name}</strong>
        </div>
        <Descriptions
          bordered
          column={2}
          size="small"
          style={{ marginBottom: '16px' }}
          items={[
            {
              label: 'Adresă',
              children: paymentAccount.client_address
            },
            {
              label: 'C.F.',
              children: paymentAccount.client_fiscal_code
            },
            {
              label: 'TVA',
              children: paymentAccount.client_vat
            },
            {
              label: 'Banca beneficiară',
              children: paymentAccount.client_beneficiary_bank
            },
            {
              label: 'SWIFT Code',
              children: paymentAccount.client_swift_code,
              span: 2
            }
          ]}
        />

        {/* Services Table */}
        <Table
          columns={serviceColumns}
          dataSource={paymentAccount.services}
          pagination={false}
          rowKey="id"
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ backgroundColor: '#ffeb3b' }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>Total:</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>
                    {formatPrice(
                      paymentAccount.total_without_vat,
                      paymentAccount.currency
                    )}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <strong>
                    {formatPrice(
                      paymentAccount.vat_amount,
                      paymentAccount.currency
                    )}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <strong>
                    {formatPrice(paymentAccount.total, paymentAccount.currency)}
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />

        {/* Total Section */}
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#ffeb3b'
          }}
        >
          <div>
            <strong>
              Total:{' '}
              {formatPrice(paymentAccount.total, paymentAccount.currency)}
            </strong>
          </div>
          <div style={{ marginTop: '8px' }}>
            <strong>
              Total spre achitare -{' '}
              {formatPrice(paymentAccount.total, paymentAccount.currency)} (
              {formatAmountInWords(
                paymentAccount.total,
                paymentAccount.currency
              )}
              ) {paymentAccount.currency}, 00 eurocenți
            </strong>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>{paymentAccount.supplier_name}</div>
          <div>
            <DayLabel date={paymentAccount.created_at} />
          </div>
        </div>

        {/* Close Button */}
        <Flex justify="flex-end" style={{ marginTop: '24px' }}>
          <Button onClick={onCancel}>{t('buttons.close')}</Button>
        </Flex>
      </div>
    </Modal>
  )
}
